import React, { useState } from "react";
import { ChevronLeft, ChevronRight, X, Calendar, Users } from "lucide-react";
import { formatTimeOnly, getISTDateParts } from "../../utils/datetime";
import "../../css/dashboard/calendarView.css";

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

export default function CalendarView({ visits = [], meetings = [] }) {
  const [currentDate, setCurrentDate]   = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null); // { year, month, day }

  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const today = new Date();

  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDay    = (y, m) => new Date(y, m, 1).getDay();

  const daysInMonth   = getDaysInMonth(year, month);
  const firstDay      = getFirstDay(year, month);
  const prevMonthDays = getDaysInMonth(year, month - 1);

  const changeMonth = (delta) =>
    setCurrentDate(new Date(year, month + delta, 1));

  /* Build event maps  key = "YYYY-M-D" */
  const visitMap   = {};
  const meetingMap = {};

  // Bucket by the event's IST calendar day — not the viewer's device timezone,
  // so a visit near midnight IST always lands on the correct day cell.
  visits.forEach(v => {
    if (!v.scheduled_date) return;
    const ist = getISTDateParts(v.scheduled_date);
    if (ist.year === year && ist.month - 1 === month) {
      const key = `${year}-${month}-${ist.day}`;
      (visitMap[key] = visitMap[key] || []).push(v);
    }
  });

  meetings.forEach(m => {
    if (!m.meetingScheduled) return;
    const ist = getISTDateParts(m.meetingScheduled);
    if (ist.year === year && ist.month - 1 === month) {
      const key = `${year}-${month}-${ist.day}`;
      (meetingMap[key] = meetingMap[key] || []).push(m);
    }
  });

  /* Events for selected date */
  const selKey = selectedDate
    ? `${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`
    : null;
  const selVisits   = selKey ? (visitMap[selKey]   || []) : [];
  const selMeetings = selKey ? (meetingMap[selKey] || []) : [];
  const hasSelEvents = selVisits.length > 0 || selMeetings.length > 0;

  const handleDayClick = (d) => {
    const key = `${year}-${month}-${d}`;
    const hasEvents = visitMap[key] || meetingMap[key];
    if (!hasEvents) { setSelectedDate(null); return; }
    setSelectedDate({ year, month, day: d });
  };

  const renderGrid = () => {
    const cells = [];

    // Prev month trailing
    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push(
        <div key={`prev-${i}`} className="calendar-day other-month">
          <span className="day-num">{prevMonthDays - i}</span>
        </div>
      );
    }

    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      const isToday =
        year  === today.getFullYear() &&
        month === today.getMonth()    &&
        d     === today.getDate();

      const key        = `${year}-${month}-${d}`;
      const hasVisit   = !!visitMap[key];
      const hasMeeting = !!meetingMap[key];
      const isSelected =
        selectedDate &&
        selectedDate.year  === year  &&
        selectedDate.month === month &&
        selectedDate.day   === d;

      cells.push(
        <div
          key={d}
          className={[
            "calendar-day",
            isToday    ? "today"    : "",
            isSelected ? "selected" : "",
            (hasVisit || hasMeeting) ? "has-events" : "",
          ].filter(Boolean).join(" ")}
          onClick={() => handleDayClick(d)}
        >
          <span className="day-num">{d}</span>
          {(hasVisit || hasMeeting) && (
            <div className="calendar-dots">
              {hasVisit   && <span className="dot dot-visit"   title="Site Visit" />}
              {hasMeeting && <span className="dot dot-meeting" title="Meeting"    />}
            </div>
          )}
        </div>
      );
    }

    // Next month leading (fill to 42 cells)
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      cells.push(
        <div key={`next-${d}`} className="calendar-day other-month">
          <span className="day-num">{d}</span>
        </div>
      );
    }

    return cells;
  };

  return (
    <div className="calendar-card">
      {/* Navigation */}
      <div className="calendar-header">
        <button onClick={() => changeMonth(-1)} aria-label="Previous month">
          <ChevronLeft size={18} />
        </button>
        <h3>{MONTH_NAMES[month]} {year}</h3>
        <button onClick={() => changeMonth(1)} aria-label="Next month">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Weekdays */}
      <div className="calendar-weekdays">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(day => (
          <div key={day} className="weekday">{day}</div>
        ))}
      </div>

      {/* Grid */}
      <div className="calendar-grid">{renderGrid()}</div>

      {/* Legend */}
      <div className="calendar-legend">
        <span className="legend-item"><span className="dot dot-visit"   /> Site Visit</span>
        <span className="legend-item"><span className="dot dot-meeting" /> Meeting</span>
      </div>

      {/* ── Date Popup ── */}
      {selectedDate && (
        <div className="cal-popup-backdrop" onClick={() => setSelectedDate(null)}>
          <div className="cal-popup" onClick={(e) => e.stopPropagation()}>
            {/* Popup header */}
            <div className="cal-popup-header">
              <span className="cal-popup-title">
                {MONTH_NAMES[selectedDate.month]} {selectedDate.day}, {selectedDate.year}
              </span>
              <button className="cal-popup-close" onClick={() => setSelectedDate(null)}>
                <X size={16} />
              </button>
            </div>

            {/* Popup body */}
            <div className="cal-popup-body">
              {!hasSelEvents && (
                <p className="cal-popup-empty">No events on this day.</p>
              )}

              {selVisits.length > 0 && (
                <div className="cal-popup-section">
                  <div className="cal-popup-section-title">
                    <span className="dot dot-visit" /> Site Visits ({selVisits.length})
                  </div>
                  {selVisits.map((v, i) => {
                    const propName =
                      typeof v.property_id === "object"
                        ? v.property_id?.propertyName
                        : null;
                    return (
                      <div key={i} className="cal-popup-event cal-popup-event--visit">
                        <Calendar size={13} />
                        <div>
                          <p className="cal-event-time">
                            {v.scheduled_date
                              ? formatTimeOnly(v.scheduled_date)
                              : "Time TBD"}
                          </p>
                          <p className="cal-event-name">
                            {propName || "Property visit"}
                          </p>
                          {v.leadName && <p className="cal-event-sub">Lead: {v.leadName}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {selMeetings.length > 0 && (
                <div className="cal-popup-section">
                  <div className="cal-popup-section-title">
                    <span className="dot dot-meeting" /> Meetings ({selMeetings.length})
                  </div>
                  {selMeetings.map((m, i) => (
                    <div key={i} className="cal-popup-event cal-popup-event--meeting">
                      <Users size={13} />
                      <div>
                        <p className="cal-event-time">
                          {m.meetingScheduled
                            ? formatTimeOnly(m.meetingScheduled)
                            : "Time TBD"}
                        </p>
                        <p className="cal-event-name">{m.clientName || "Client meeting"}</p>
                        {m.remarks && (
                          <p className="cal-event-sub">{m.remarks.slice(0, 60)}{m.remarks.length > 60 ? "…" : ""}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
